import {UseHealthSearchBarProps} from './types';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useGetHealthProvidersNameQuery} from 'presentation/modules/booking/shared/queries/useGetHealthProvidersNameQuery';
import {useGetHealthDatesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthDatesQuery';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {useQueryParams} from 'presentation/pages';
import {
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {AppointmentBookingEnum, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {useDistanceSliderStore} from 'presentation/modules/booking/shared/ui/components/DistanceSlider/store';
import {useEffect, useState} from 'react';
import {ShiftTypeEnum} from 'presentation/modules/booking/exam/constants/shiftTypeEnum';
import {QueryParamsEnum} from 'presentation/constants';
import {createPeriodsConfig} from 'presentation/modules/booking/shared/utils/createPeriodsConfig';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';
import {format, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {useProviderNamesStore} from 'presentation/modules/booking/shared/store/useProviderNamesStore';
import {MIN_DISTANCE_IN_KM_HEALTH} from './constants';

export const useHealthSearchBar = ({activeStep}: UseHealthSearchBarProps) => {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const getProviderNamesState = useProviderNamesStore(state => state.providersName);
	const openDrawerState = useOpenDrawerFilterStore(state => state.openDrawerState);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const distanceSlider = useDistanceSliderStore(state => state.distanceSlider);
	const setDistanceSlider = useDistanceSliderStore(state => state.setDistanceSlider);
	const {SEARCH_PARAMS, setSearchParams, clearSearch} = useQueryParams();
	const {query, shift, localityDistance, autocompleteQuery} = SEARCH_PARAMS;
	const [shiftSelected, setShiftSelected] = useState<ShiftTypeEnum>(
		(shift as ShiftTypeEnum) ?? ShiftTypeEnum.ALL,
	);
	const externalOpenDateModal = useExternalOpenDateModalStore(
		state => state.externalOpenDateModal,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const disabledSelectionDate = useDisabledSelectionDateStore(
		state => state.disabledSelectionDate,
	);

	const {isManualLocation, dateAt} = bookingData;

	const {PROFESSIONAL_STEP, UNIT_STEP, SPECIALITY_OR_EXAM_STEP} = getHealthStepPosition({
		isManualLocation,
	});

	const {isError: providersNameHasError, isFetching: providersNameIsLoading} =
		useGetHealthProvidersNameQuery({enabled: activeStep === PROFESSIONAL_STEP});

	const {
		data: healthAvailableDatesList,
		isFetching: healthDatesIsLoading,
		isError: healthDatesIsError,
	} = useGetHealthDatesQuery({
		enabled: activeStep === PROFESSIONAL_STEP,
		isTelemedicine: false,
	});

	const shouldRenderAutocomplete =
		activeStep === PROFESSIONAL_STEP && !providersNameHasError && !!getProviderNamesState;

	const isGettingProviderNames = providersNameIsLoading || healthDatesIsLoading;
	const providersNameOptions =
		getProviderNamesState?.medicos?.map(medico => ({
			id: medico?.cdPrestadorFisico,
			label: medico?.nmMedico,
		})) ?? [];

	const showDistanceDrawer = Boolean(activeStep === UNIT_STEP);
	const showFilter = showDistanceDrawer;
	const distanceFilterAtMaxDistance = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS;

	const distanceFilterIsActive = localityDistance;
	const filterIsActive = distanceFilterIsActive;
	const drawerButtonTagId = showDistanceDrawer
		? AppointmentBookingEnum.ALTEROU_DISTANCIA_POR_FILTRO
		: OdontoBookingTaggingEnum.CLICK_FILTRAR_UNIDADES;

	const showFilterByLetter = !query && activeStep === SPECIALITY_OR_EXAM_STEP;
	const showCurrentLocation = activeStep === UNIT_STEP;
	const showProfessionalFilters = activeStep === PROFESSIONAL_STEP;
	const shouldClearShiftFilter = activeStep === UNIT_STEP;

	const availableDatesLoadingOrError = healthDatesIsLoading || healthDatesIsError;
	const availableDates = availableDatesLoadingOrError ? [] : healthAvailableDatesList;

	const currentSelectedDate = dateAt ? parse(dateAt, 'P', 0, {locale: ptBR}) : undefined;

	const distanceValue = useDistanceSliderStore(state => state.distanceSlider);
	const distanceLabel = `${distanceValue}km`;

	const PERIODS = createPeriodsConfig({
		shiftSelected,
		onShiftChange: (shift: ShiftTypeEnum) => setShiftSelected(shift),
	});

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleCloseDrawer = (abandon?: boolean) => {
		setOpenDrawerState(false);
		if (abandon) {
			setShiftSelected(ShiftTypeEnum.ALL);
			setDistanceSlider(Number(localityDistance));
		}
	};

	const handleClearDistance = () => {
		setDistanceSlider(25);
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);

			if (isManualLocation) {
				prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
			} else {
				prev.set(QueryParamsEnum.LOCALITY_DISTANCE, MIN_DISTANCE_IN_KM_HEALTH);
			}
			return prev;
		});
		handleCloseDrawer();
	};

	const handleConfirmDistance = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, distanceSlider.toString());
			prev.set(QueryParamsEnum.DISTANCE_ORIGIN, MANUAL_DISTANCE_ORIGIN);
			return prev;
		});

		handleCloseDrawer();
	};

	const handleShiftButton = (shiftType: ShiftTypeEnum | string) => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SHIFT, shiftType);
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShift = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
	};

	const handleConfirmShift = () => {
		handleShiftButton(shiftSelected);
		handleCloseDrawer();
	};

	const handleChangeDate = (selectedDate: Date | null) => {
		setSearchParams(prev => {
			if (selectedDate) {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: format(selectedDate, 'P', {locale: ptBR}),
				}));

				prev.set(QueryParamsEnum.CALENDAR_ACTIVE, 'true');
			} else {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: '',
				}));

				prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			}

			return prev;
		});
		setExternalOpenDateModal(false);
	};

	const clearSearchParams = () => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShiftAndSearch = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
		clearSearchParams();
		if (clearSearch) {
			clearSearch();
		}
		setSearchParams(prev => {
			setBookingData(prevValue => ({
				...prevValue,
				dateAt: '',
			}));

			prev.set(QueryParamsEnum.SEARCH_QUERY, 'clear');
			prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			if (autocompleteQuery) prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
			return prev;
		});
	};

	const hideElementProfessionalStepIsFavoriteDoctors =
		bookingData?.isFavoriteDoctorsFlow && activeStep === PROFESSIONAL_STEP;

	const hideElementUnitStepIsFavoriteDoctors =
		bookingData?.isFavoriteDoctorsFlow && activeStep === UNIT_STEP;

	useEffect(() => {
		if (shouldClearShiftFilter) {
			handleClearShift();
		}
	}, [activeStep]);

	useEffect(() => {
		if (SEARCH_PARAMS.shift === '') {
			handleCloseDrawer(!openDrawerState);
		}
	}, [SEARCH_PARAMS.shift]);

	return {
		shouldRenderAutocomplete,
		getProviderNamesState,
		isGettingProviderNames,
		providersNameOptions,
		showFilter,
		handleOpenDrawer,
		filterIsActive,
		showDistanceDrawer,
		drawerButtonTagId,
		openDrawerState,
		handleCloseDrawer,
		distanceFilterAtMaxDistance,
		handleConfirmDistance,
		handleClearDistance,
		showFilterByLetter,
		showCurrentLocation,
		showProfessionalFilters,
		shift,
		PERIODS,
		handleClearShift,
		handleConfirmShift,
		setExternalOpenDateModal,
		externalOpenDateModal,
		availableDates,
		currentSelectedDate,
		handleChangeDate,
		disabledSelectionDate,
		handleClearShiftAndSearch,
		distanceLabel,
		hideElementProfessionalStepIsFavoriteDoctors,
		hideElementUnitStepIsFavoriteDoctors,
	};
};
